import 'es5-shim';
import 'es5-shim/es5-sham';
import 'es6-shim';
import 'core-js/stable';
import 'normalize.css/normalize.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import history from './utils/history';
import configureStore from './store/configureStore';
import { readAuthFromStorage } from './reducers/auth';
import { handleRedirectCallback, getAccessToken, getUser } from './services/auth0';
import { writeSession, clearSessionStorage } from './actions/authThunks';
import { startTokenRefresh } from './services/tokenRefresh';

// Containers.
import Root from './containers/utils/Root';

// Returns true when Auth0 has just sent the user back from Universal Login.
const hasRedirectParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
};

/**
 * Exchanges the Auth0 code for a session. Failures are swallowed on purpose —
 * the user lands on the login screen, which beats an unrendered app.
 */
const completeLogin = async () => {
  try {
    await handleRedirectCallback();

    const { access_token, expires_in } = await getAccessToken();
    const profile = await getUser();
    if (!profile) {
      throw new Error('Auth0 returned no user profile');
    }

    writeSession(access_token, expires_in, profile);
  } catch (error) {
    console.error('*** Login callback failed:%o', error);
    clearSessionStorage();
  } finally {
    // The code is single-use: leaving it in the URL makes a reload retry a spent one.
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};

/**
 * Boots the app. The order is the point:
 *
 *  1. Finish any redirect callback before the router mounts — ProtectedRoute
 *     would redirect to "/" and strip the `?code=` before it could be exchanged.
 *  2. Then read the session and build the store with it, so the store sees what
 *     this bootstrap just wrote rather than what existed at import time.
 *  3. Render.
 *
 * No "checking session" screen: the blob is read synchronously, so the first
 * paint already knows whether the user is logged in.
 */
const start = async () => {
  if (hasRedirectParams()) {
    await completeLogin();
  }

  const store = configureStore({ auth: readAuthFromStorage() });

  // No-op when logged out; started here rather than in a component so
  // StrictMode's double mount cannot start two.
  startTokenRefresh();

  const root = createRoot(document.getElementById('app')!);
  // The two History types differ (createURL/encodeLocation) but are runtime-compatible.
  root.render(<Root history={history as never} store={store}/>);
};

start();
